"use client";

import React from "react";
import { FileText, ShieldCheck, ArrowRight, CircleCheck, SlidersHorizontal, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HubHeroCopy {
  categoryBadge: string;
  standardBadge: string;
  title: string;
  lead: string;
  resourcesBtn: string;
  specMatrix: string;
  isoStandardBadge: string;
  geometryChip: string;
  mtrNote: string;
  calculationGuides: string;
  readGuide: string;
  specStrip: {
    meshLabel: string;
    wireLabel: string;
    micronLabel: string;
    weaveLabel: string;
  };
  cards: Array<{
    title: string;
    desc: string;
    link: string;
    icon: typeof ShieldCheck;
  }>;
}
const copyData: Record<Lang, HubHeroCopy> = {
  en: {
    categoryBadge: "Specifications & Standards",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Stainless Steel Wire Mesh Specifications & Technical Standards",
    lead: "Access engineering standards, ASTM E2016 and ISO 9044 tolerance criteria, and weave structural parameters. We supply precision industrial wire mesh with wire diameters from 0.020 to 2.0 mm, mesh counts from 1 to 635, and custom fabrication options.",
    resourcesBtn: "Technical Resources",
    specMatrix: "Spec Parameter Matrix",
    isoStandardBadge: "ISO 9044 / ASTM",
    geometryChip: "Aperture & Open Area",
    mtrNote: "EN 10204 3.1 on request",
    calculationGuides: "Calculation Guides",
    readGuide: "Read Technical Guide",
    specStrip: {
      meshLabel: "Mesh Count (n)",
      wireLabel: "Wire Diameter",
      micronLabel: "Micron Rating",
      weaveLabel: "Weave Patterns"
    },
    cards: [{
      title: "ASTM E2016 Standards",
      desc: "Standard specification for industrial woven wire cloth aperture tolerances, permissible flaws, and inspection criteria.",
      link: "/AstmE2016ExplainedPage",
      icon: ShieldCheck
    }, {
      title: "ISO 9044 Standards",
      desc: "International aperture sizing requirements, wire diameter tolerances, and open area calculation rules.",
      link: "/ISOWireMeshStandardsPage",
      icon: FileText
    }, {
      title: "Material Verification",
      desc: "Chemical composition analysis and inspection methods for 304, 316, and 316L stainless steel alloys.",
      link: "/StainlessSteelMaterialVerificationPage",
      icon: ShieldCheck
    }, {
      title: "Aperture Calculations",
      desc: "Formulae and conversion references for mesh count, wire diameter, open area percentage, and micron ratings.",
      link: "/WireMeshApertureMeasurementPage",
      icon: SlidersHorizontal
    }]
  },
  zh: {
    categoryBadge: "工程规范与标准",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "不锈钢丝网工程规格与技术制造标准",
    lead: "查阅权威工程标准、ASTM E2016 与 ISO 9044 编织公差规范及经纬几何参数。我们提供目数 1 至 635 目、丝径 0.020 至 2.0 mm 的精密工业不锈钢丝网与定制组件加工支持。",
    resourcesBtn: "技术资源中心",
    specMatrix: "技术参数矩阵",
    isoStandardBadge: "ISO 9044 / ASTM",
    geometryChip: "网孔几何与开孔率",
    mtrNote: "可按订单要求提供 EN 10204 3.1 报告",
    calculationGuides: "计算指南",
    readGuide: "阅读技术指南",
    specStrip: {
      meshLabel: "目数范围 (Mesh)",
      wireLabel: "丝径公差范围",
      micronLabel: "过滤精度等级",
      weaveLabel: "编织形式"
    },
    cards: [{
      title: "ASTM E2016 规范解读",
      desc: "工业不锈钢编织丝网孔径公差、经纬丝偏差与疵点等级判定标准。",
      link: "/AstmE2016ExplainedPage",
      icon: ShieldCheck
    }, {
      title: "ISO 9044 国际标准",
      desc: "工业金属丝网孔径公差测试、丝径校核与开孔率计算国际标准。",
      link: "/ISOWireMeshStandardsPage",
      icon: FileText
    }, {
      title: "材料成分验证与检验",
      desc: "304、316 与 316L 不锈钢材料化学成分要求、耐蚀评定与光谱检测说明。",
      link: "/StainlessSteelMaterialVerificationPage",
      icon: ShieldCheck
    }, {
      title: "孔径与开孔率计算",
      desc: "目数、线径、公称开孔尺寸与三维流动压降工程换算模型。",
      link: "/WireMeshApertureMeasurementPage",
      icon: SlidersHorizontal
    }]
  },
  es: {
    categoryBadge: "Especificaciones y normas",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Especificaciones y normas técnicas de mallas de acero inoxidable",
    lead: "Consulte normas de ingeniería, criterios de tolerancia según ASTM E2016 e ISO 9044 y parámetros estructurales del tejido. Suministramos mallas de alambre de 1 a 635 mesh, diámetros de 0.020 a 2.0 mm y fabricación a medida.",
    resourcesBtn: "Recursos técnicos",
    specMatrix: "Matriz de parámetros técnicos",
    isoStandardBadge: "ISO 9044 / ASTM",
    geometryChip: "Abertura y área abierta",
    mtrNote: "EN 10204 3.1 disponible según pedido",
    calculationGuides: "Guías de cálculo",
    readGuide: "Leer guía técnica",
    specStrip: {
      meshLabel: "Rango de malla (Mesh)",
      wireLabel: "Diámetro del alambre",
      micronLabel: "Grado de retención",
      weaveLabel: "Patrones de tejido"
    },
    cards: [{
      title: "Norma ASTM E2016",
      desc: "Especificación para tolerancias dimensionales, defectos admisibles y criterios de inspección en telas metálicas.",
      link: "/AstmE2016ExplainedPage",
      icon: ShieldCheck
    }, {
      title: "Normas ISO 9044",
      desc: "Requisitos internacionales de abertura, tolerancias de diámetro y cálculo de área abierta.",
      link: "/ISOWireMeshStandardsPage",
      icon: FileText
    }, {
      title: "Verificación de materiales",
      desc: "Composición química y pautas de control para aceros inoxidables 304, 316 y 316L.",
      link: "/StainlessSteelMaterialVerificationPage",
      icon: ShieldCheck
    }, {
      title: "Cálculos de abertura",
      desc: "Fórmulas y conversión para número de malla, diámetro de alambre, área abierta y micras.",
      link: "/WireMeshApertureMeasurementPage",
      icon: SlidersHorizontal
    }]
  },
  de: {
    categoryBadge: "Spezifikationen & Normen",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Drahtgewebe-Spezifikationen & technische Normen aus Edelstahl",
    lead: "Nutzen Sie anerkannte Industrienormen, Toleranzkriterien nach ASTM E2016 und ISO 9044 sowie Gewebestrukturdaten. Wir liefern Präzisionsdrahtgewebe von 1 bis 635 Mesh, Drahtdurchmesser von 0.020 bis 2.0 mm und individuelle Fertigung.",
    resourcesBtn: "Technische Ressourcen",
    specMatrix: "Technische Parametermatrix",
    isoStandardBadge: "ISO 9044 / ASTM",
    geometryChip: "Maschenweite & offene Siebfläche",
    mtrNote: "EN 10204 3.1 auf Anfrage erhältlich",
    calculationGuides: "Berechnungsleitfäden",
    readGuide: "Leitfaden lesen",
    specStrip: {
      meshLabel: "Maschenzahl (Mesh)",
      wireLabel: "Drahtdurchmesser",
      micronLabel: "Filterfeinheit",
      weaveLabel: "Webarten"
    },
    cards: [{
      title: "ASTM E2016 Normen",
      desc: "Standardspezifikation für Maschenweitentoleranzen, zulässige Webfehler und Prüfkriterien bei Industriegeweben.",
      link: "/AstmE2016ExplainedPage",
      icon: ShieldCheck
    }, {
      title: "ISO 9044 Normen",
      desc: "Internationale Anforderungen an Maschenweiten, Drahtdurchmessertoleranzen und offene Siebflächen.",
      link: "/ISOWireMeshStandardsPage",
      icon: FileText
    }, {
      title: "Werkstoffprüfung",
      desc: "Chemische Zusammensetzung und Prüfverfahren für Edelstahllegierungen 304, 316 und 316L.",
      link: "/StainlessSteelMaterialVerificationPage",
      icon: ShieldCheck
    }, {
      title: "Öffnungsberechnungen",
      desc: "Formeln und Umrechnungsmodelle für Maschenzahl, Drahtdurchmesser, offene Fläche und Mikronwerte.",
      link: "/WireMeshApertureMeasurementPage",
      icon: SlidersHorizontal
    }]
  },
  fr: {
    categoryBadge: "Spécifications et normes",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Spécifications et normes techniques des toiles en acier inoxydable",
    lead: "Consultez les normes techniques, les critères de tolérance ASTM E2016 et ISO 9044 ainsi que les données de tissage. Nous fournissons des toiles métalliques industrielles de 1 à 635 mesh, fils de 0.020 à 2.0 mm et solutions sur mesure.",
    resourcesBtn: "Ressources techniques",
    specMatrix: "Matrice des paramètres techniques",
    isoStandardBadge: "ISO 9044 / ASTM",
    geometryChip: "Ouverture et surface ouverte",
    mtrNote: "EN 10204 3.1 sur demande selon commande",
    calculationGuides: "Guides de calcul",
    readGuide: "Consulter le guide",
    specStrip: {
      meshLabel: "Nombre de mailles (Mesh)",
      wireLabel: "Diamètre de fil",
      micronLabel: "Seuil de filtration",
      weaveLabel: "Types de tissage"
    },
    cards: [{
      title: "Normes ASTM E2016",
      desc: "Spécification standard des tolérances d'ouverture, des défauts admissibles et des critères de contrôle.",
      link: "/AstmE2016ExplainedPage",
      icon: ShieldCheck
    }, {
      title: "Normes ISO 9044",
      desc: "Exigences internationales pour l'ouverture des mailles, tolérances de fil et calcul du pourcentage de vide.",
      link: "/ISOWireMeshStandardsPage",
      icon: FileText
    }, {
      title: "Vérification des matériaux",
      desc: "Composition chimique et procédures d'analyse pour les nuances d'acier inoxydable 304, 316 et 316L.",
      link: "/StainlessSteelMaterialVerificationPage",
      icon: ShieldCheck
    }, {
      title: "Calculs d'ouverture",
      desc: "Formules de conversion pour le nombre de mailles, le diamètre de fil, la surface ouverte et les microns.",
      link: "/WireMeshApertureMeasurementPage",
      icon: SlidersHorizontal
    }]
  },
  pt: {
    categoryBadge: "Especificações e normas",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Especificações e normas técnicas de telas de aço inoxidável",
    lead: "Acesse normas de engenharia, critérios de tolerância ASTM E2016 e ISO 9044 e parâmetros estruturais da tecelagem. Fornecemos telas industriais de 1 a 635 mesh, diâmetros de 0.020 a 2.0 mm e opções de fabricação sob medida.",
    resourcesBtn: "Recursos técnicos",
    specMatrix: "Matriz de parâmetros técnicos",
    isoStandardBadge: "ISO 9044 / ASTM",
    geometryChip: "Abertura e área aberta",
    mtrNote: "EN 10204 3.1 disponível sob consulta",
    calculationGuides: "Guias de cálculo",
    readGuide: "Ler guia técnico",
    specStrip: {
      meshLabel: "Faixa de malha (Mesh)",
      wireLabel: "Diâmetro do arame",
      micronLabel: "Grau de filtração",
      weaveLabel: "Tipos de tecelagem"
    },
    cards: [{
      title: "Normas ASTM E2016",
      desc: "Especificação padrão para tolerâncias de abertura, defeitos permitidos e critérios de inspeção de telas tecidas.",
      link: "/AstmE2016ExplainedPage",
      icon: ShieldCheck
    }, {
      title: "Normas ISO 9044",
      desc: "Requisitos internacionais para dimensionamento de aberturas, tolerâncias de arame e área aberta.",
      link: "/ISOWireMeshStandardsPage",
      icon: FileText
    }, {
      title: "Verificação de materiais",
      desc: "Composição química e critérios de inspeção para ligas de aço inoxidável 304, 316 e 316L.",
      link: "/StainlessSteelMaterialVerificationPage",
      icon: ShieldCheck
    }, {
      title: "Cálculos de abertura",
      desc: "Fórmulas e modelos de conversão para contagem de malha, diâmetro de arame, área aberta e micragens.",
      link: "/WireMeshApertureMeasurementPage",
      icon: SlidersHorizontal
    }]
  }
};
export default function HubHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="hubhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container">
        {/* Hero Grid Content (7 / 5 Layout) */}
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Main Content Column (7 Columns) */}
          <div className="lg:col-span-7 flex flex-col items-start">
            {/* Badges row: EXACTLY two badges, both variant="outline" */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.categoryBadge}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.standardBadge}</span>
              </Badge>
            </div>

            <h1 className="font-default text-balance mf-h1 text-foreground">
              {copy.title}
            </h1>

            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {copy.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild>
                <Link to="/RequestAQuotePage">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild>
                <Link to="/TechnicalResourcesPage">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{copy.resourcesBtn}</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Visual Spec Panel (5 Columns on lg) */}
          <div className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual Container */}
              <div className="relative mf-card-media mf-ratio-hero w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wire_mesh_technical_spec.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wire_mesh_technical_spec.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wire_mesh_technical_spec.png-wvc-sizes" alt="Precision woven wire mesh geometry diagram" className="h-full w-full object-cover object-center" loading="eager" fetchPriority="high" decoding="async" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.geometryChip}</span>
                </div>
              </div>

              {/* Single Footer Line */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.mtrNote}</span>
                  </div>
                  <Link to="/CalculationGuidesPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{copy.calculationGuides}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          <div className="mf-spec-strip__item">
            <span className="mf-spec-strip__label">{copy.specStrip.meshLabel}</span>
            <span className="mf-spec-strip__value">1 – 635 Mesh</span>
          </div>
          <div className="mf-spec-strip__item">
            <span className="mf-spec-strip__label">{copy.specStrip.wireLabel}</span>
            <span className="mf-spec-strip__value">0.020 – 2.0 mm</span>
          </div>
          <div className="mf-spec-strip__item">
            <span className="mf-spec-strip__label">{copy.specStrip.micronLabel}</span>
            <span className="mf-spec-strip__value">1 µm – 25 mm</span>
          </div>
          <div className="mf-spec-strip__item">
            <span className="mf-spec-strip__label">{copy.specStrip.weaveLabel}</span>
            <span className="mf-spec-strip__value">Plain / Twill / Dutch</span>
          </div>
        </div>

        {/* 4-Card Nav Grid for Hub Navigation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {copy.cards.map((item, i) => {
          const IconComponent = item.icon;
          return <Link key={i} data-index={i} to={item.link} className="group p-5 bg-card text-card-foreground rounded-lg border border-border hover:border-primary/50 transition-colors flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                    <IconComponent className="size-5" aria-hidden="true" />
                  </div>
                  <h2 className="mf-h3 text-foreground group-hover:text-primary transition-colors flex items-center justify-between">
                    <span>{item.title}</span>
                    <ChevronRight className="size-4 text-primary shrink-0" aria-hidden="true" />
                  </h2>
                  <p className="mf-caption text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>
                <div className="pt-4 mf-caption font-mono text-primary font-medium flex items-center gap-1">
                  <span>{copy.readGuide}</span>
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </div>
              </Link>;
        })}
        </div>
      </div>
    </section>;
}